import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Label, Spinner } from "@fluentui/react-components";
import { IAppealProps, IAppealInfoResponse, IAppealFormInfo, IAppealSubmissionRequest, IAppealSubmissionProps, IAccountAppealSubmissionRequest, IAppealInfoRequest, IAccountAppealInfoRequest, IAccountAppealInfoResponse } from '../model/interface';
import { getAccountAppealInfo, getInitialAppealerInfo, postAccountAppeal, postAppeal } from '../utils/api';
import axios from 'axios';
import { getAppealLandingHeader, getAppealLandingSubHeader, getAppealLandingSubHeaderTwo, getAppealType, getDefaultAppealFormInfo, getDefaultAppealSubmissionInfo, getDefaultInitialResponseInfo, getInitialAppealFormInfo, getInitialPage } from '../utils/appealUtil';
import { VerifyCaptchaResponse, useAfdCaptcha, CaptchaPopup, getStubUrl } from 'uicomponents';
import 'uicomponents/index.css';
import { AppealPage, AppealType } from '../model/enum';
import { APPEALS_SOURCE } from '../constants/constants';
import { isEmailValid, isJustificationValid, isNameValid, isUserIdValid } from '../utils/formValidator';
import ErrorPage from './error/ErrorPage';
import AppealLanding from './appeallanding/AppealLanding';
import AppealForm from './appealForm/AppealForm';
import AppealSubmission from './appealSubmission/AppealSubmission';
import AppealButtonRow from './appealButtonRow/AppealButtonRow';
import AppealHip from './appealHip/AppealHip';
import ErrorBox from './error/ErrorBox';
import AppealExpiry from './appealExpiry/AppealExpiry';
import './Appeal.scss';
import AppealPreSubmission from './appealPreSubmission/AppealPreSubmission';
import { useFeatureFlags, useSettingsLoaded } from '../ApplicationSettings';
import env from '../utils/env';

export default function Appeal(props: IAppealProps) {
  const featureFlags = useFeatureFlags();
  const settingsLoaded = useSettingsLoaded();
  const isAfdWafEnabled = featureFlags?.EnableAfdWafCaptcha ?? true;
  const shouldUseAfdCaptcha = settingsLoaded && isAfdWafEnabled;
  const shouldUseLegacyHip = settingsLoaded && !isAfdWafEnabled;
  const disableAutoSubmit = featureFlags?.DisableAfdCaptchaAutoSubmit ?? false;
  const captcha = useAfdCaptcha(env.REACT_APP_BACKEND_URL, { autoSubmit: !disableAutoSubmit });
  let [initialAppealerInfo, setInitialAppealerInfo] = useState<IAppealInfoResponse>(getDefaultInitialResponseInfo());
  let [accountAppealInfo, setAccountAppealInfo] = useState<IAccountAppealInfoResponse>();
  let [formInfo, setFormInfo] = useState<IAppealFormInfo>(getDefaultAppealFormInfo());
  let [submissionInfo, setSubmissionInfo] = useState<IAppealSubmissionProps>(getDefaultAppealSubmissionInfo());
  let [formIsValid, setFormIsValid] = useState<boolean>(false);
  let [preSubmissionIsValid, setPreSubmissionIsValid] = useState<boolean>(false);
  let [checkboxChecked, setCheckboxChecked] = useState<boolean>(false);
  let [currentPage, setCurrentPage] = useState<AppealPage>(AppealPage.Landing);
  let [appealType, setAppealType] = useState<AppealType>(AppealType.Unknown);
  let [isLoading, setIsLoading] = useState(true);
  let [isRequestProcessing, setIsRequestProcessing] = useState(false);
  let [hipSolved, setHipSolved] = useState(false);
  let [hasInitialError, setHasInitialError] = useState(false);
  let [hasSubmissionError, setHasSubmissionError] = useState(false);
  let [hasHipError, setHasHipError] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (props.formId) {
      if (!settingsLoaded) {
        return;
      }
      const initialRequest: IAppealInfoRequest =
      {
        formId: props.formId,
        locale: props.locale
      };
      const fetchAppealInfo = async () => {
        const response = await getInitialAppealerInfo(initialRequest);
        setInitialAppealerInfo(response);
        setFormInfo(getInitialAppealFormInfo(response, props.formId));
        setHasInitialError(response.hasInitialError);
        setAppealType(getAppealType(response.appealIdType));
        setCurrentPage(getInitialPage(response));
        setIsLoading(false);
      };
      if (isAfdWafEnabled) {
        captcha.submit(fetchAppealInfo);
      } else {
        fetchAppealInfo();
      }
    }
    else {
      setAppealType(AppealType.Account);
      setInitialAppealerInfo({ ...initialAppealerInfo, appealPeriodValid: true });
      setIsLoading(false);
    }
  }, [settingsLoaded, isAfdWafEnabled]); //eslint-disable-line react-hooks/exhaustive-deps

  const handleCaptchaSubmit = (response: VerifyCaptchaResponse | undefined) => {
    if (response?.solved) {
      setHasHipError(false);
      setHipSolved(true);
      sessionStorage.setItem("token", response.accessToken.token);
      sessionStorage.setItem("tokenExpiry", response.accessToken.expiresOn);
    }
    else {
      setHasHipError(true);
    }
  }

  const handleContinue = () => {
    if (shouldUseLegacyHip) {
      validateHipStatus();
    }

    if (appealType === AppealType.Account && currentPage === AppealPage.Form) {
      if (isAfdWafEnabled) {
        captcha.submit(async () => {
          await handleAccountAppealInfoRequest();
        });
      } else {
        handleAccountAppealInfoRequest();
      }
    }

    else if (currentPage < AppealPage.Submission) {
      // When AFD is enabled, skip the HIP page (Landing → Form directly).
      let nextPage = currentPage + 1;
      if (isAfdWafEnabled && nextPage === AppealPage.Hip) {
        nextPage = AppealPage.Form;
      }
      setCurrentPage(nextPage);
    }
  }

  const handleBack = () => {
    if (shouldUseLegacyHip) {
      validateHipStatus();
    }

    if (currentPage - 1 === AppealPage.Landing) {
      setHasHipError(false);
    }

    if (currentPage - 1 === AppealPage.Form) {
      setHasSubmissionError(false);
    }

    if (currentPage > AppealPage.Landing) {
      // When AFD is enabled, skip the HIP page (Form → Landing directly).
      let prevPage = currentPage - 1;
      if (isAfdWafEnabled && prevPage === AppealPage.Hip) {
        prevPage = AppealPage.Landing;
      }
      setCurrentPage(prevPage);
    }

  }

  const nameChange = (newName: string) => {
    let newFormInfo = formInfo;
    newFormInfo = { ...formInfo, appealerName: newName };
    setFormInfo(newFormInfo);
    validateForm(newFormInfo)
  };

  const emailChange = (newEmail: string) => {
    let newFormInfo = formInfo;
    newFormInfo = { ...formInfo, contactEmail: newEmail };
    setFormInfo(newFormInfo);
    validateForm(newFormInfo)
  };

  const justificationChange = (newJustification: string) => {
    let newFormInfo = formInfo;
    newFormInfo = { ...formInfo, appealerJustification: newJustification };
    setFormInfo(newFormInfo);
    validateForm(newFormInfo)
  };

  const productChange = (newProduct: string) => {
    let newFormInfo = formInfo;
    newFormInfo = { ...formInfo, product: newProduct };
    setFormInfo(newFormInfo);
    validateForm(newFormInfo)
  };

  const userIdChange = (newUserId: string) => {
    let newFormInfo = formInfo;
    newFormInfo = { ...formInfo, userId: newUserId };
    setFormInfo(newFormInfo);
    validateForm(newFormInfo)
  };

  const suspendedEmailChange = (newSuspendedEmail: string) => {
    let newFormInfo = formInfo;
    newFormInfo = { ...formInfo, suspendedEmail: newSuspendedEmail };
    setFormInfo(newFormInfo);
    validateForm(newFormInfo)
  };

  const checkboxChange = (status: boolean) => {
    let newStatus = status;
    setCheckboxChecked(newStatus);
    validateForm(formInfo, newStatus);
  }

  const validateHipStatus = () => {
    let tokenExpiry = sessionStorage.getItem("tokenExpiry");
    if (!tokenExpiry || Date.now() > Date.parse(tokenExpiry)) {
      setHipSolved(false);
      setHasHipError(false);
    }
  }

  const validateForm = (newFormInfo: IAppealFormInfo, newCheckBoxStatus?: boolean) => {
    const nameValid = isNameValid(newFormInfo.appealerName);
    const emailValid = isEmailValid(newFormInfo.contactEmail);
    const justificationValid = isJustificationValid(newFormInfo.appealerJustification);
    const userIdValid = isUserIdValid(newFormInfo.userId);
    const productValid = accountAppealInfo?.shouldAskForProduct ? (newFormInfo.product !== null && newFormInfo.product.trim() !== "") : true;
    const checkboxValid = newCheckBoxStatus !== undefined ? newCheckBoxStatus : checkboxChecked;
    if (appealType === AppealType.Content) {
      setFormIsValid(nameValid && emailValid);
      setPreSubmissionIsValid(nameValid && emailValid && justificationValid && checkboxValid)
    }

    else if (appealType === AppealType.Account) {
      setFormIsValid(userIdValid)
      setPreSubmissionIsValid(productValid && nameValid && userIdValid && emailValid && justificationValid && checkboxValid)
    }
  }

  async function handleSubmit() {
    setHasSubmissionError(false);

    let request: IAppealSubmissionRequest = {
      formId: props.formId,
      appealerName: formInfo.appealerName,
      contactEmail: formInfo.contactEmail,
      appealerJustification: formInfo.appealerJustification
    };

    const doSubmit = async () => {
      setIsRequestProcessing(true);
      try {
        let appealResponse = await postAppeal(request);

        if (appealResponse.hasError) {
          setHasSubmissionError(true);
        } else if (appealResponse.appealNumber) {
          let newSubmissionInfo: IAppealSubmissionProps = {
            appealType: appealType,
            appealNumber: appealResponse.appealNumber,
            appealerName: formInfo.appealerName,
            contactEmail: formInfo.contactEmail,
            appealDate: appealResponse.appealDate,
            appealerJustification: formInfo.appealerJustification,
            policyViolated: initialAppealerInfo.policyViolated,
            reportId: initialAppealerInfo?.reportId,
            userId: formInfo?.userId,
            suspendedEmail: null,
            isNewSubmission: true,
            isUpdatedSubmission: false
          };
          setSubmissionInfo(newSubmissionInfo);
          setCurrentPage(AppealPage.Submission);
        }
      } catch (err: unknown) {
        // Re-throw 401 so the AFD CAPTCHA layer (captcha.submit) can intercept it
        // and trigger a new CAPTCHA challenge instead of showing a generic error.
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          throw err;
        }
        setHasSubmissionError(true);
      } finally {
        setIsRequestProcessing(false);
      }
    };

    if (isAfdWafEnabled) {
      captcha.submit(doSubmit);
    } else {
      await doSubmit();
    }
  }

  async function handleAccountAppealInfoRequest() {
    const resetFormInfo = {
      ...formInfo,
      hasSubmissionError: false,
      couldFindSuspendedUserId: true
    };
    setFormInfo(resetFormInfo);

    setIsRequestProcessing(true);

    try {
      let request: IAccountAppealInfoRequest = {
        userId: formInfo.userId,
      };

      let response = await getAccountAppealInfo(request);
      setAccountAppealInfo(response);

      const updatedFormInfo = {
        ...formInfo,
        hasSubmissionError: response.hasError,
        couldFindSuspendedUserId: response.couldFindSuspendedUserId,
        shouldAskForProduct: response.shouldAskForProduct
      };
      setFormInfo(updatedFormInfo);

      setCurrentPage((response.hasError || !response.couldFindSuspendedUserId) ? currentPage : currentPage += 1);
    } catch (err: unknown) {
      // Re-throw 401 so the AFD CAPTCHA layer can intercept and re-challenge.
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        throw err;
      }
      setFormInfo({ ...formInfo, hasSubmissionError: true });
    } finally {
      setIsRequestProcessing(false);
    }
  }

  async function handleAccountAppealSubmit() {
    setHasSubmissionError(false);

    let existingAppealNumberFound = accountAppealInfo?.appealNumber !== null && accountAppealInfo?.appealNumber !== "";

    let request: IAccountAppealSubmissionRequest = {
      appealerName: formInfo.appealerName,
      contactEmail: formInfo.contactEmail,
      appealerUserId: formInfo.userId,
      appealerJustification: formInfo.appealerJustification,
      suspendedEmail: formInfo.suspendedEmail,
      product: formInfo.product,
      locale: props.locale,
    };

    const doSubmit = async () => {
      setIsRequestProcessing(true);
      try {
        let accountAppealResponse = await postAccountAppeal(request);

        if (accountAppealResponse.hasError) {
          setHasSubmissionError(true);
        } else if (accountAppealResponse.appealNumber) {
          let newSubmissionInfo: IAppealSubmissionProps = {
            appealType: appealType,
            appealNumber: accountAppealInfo?.appealNumber
              ? accountAppealInfo?.appealNumber
              : accountAppealResponse.appealNumber,
            appealerName: formInfo.appealerName,
            contactEmail: formInfo.contactEmail,
            appealDate: accountAppealResponse.appealDate,
            appealerJustification: formInfo.appealerJustification,
            policyViolated: "",
            reportId: "",
            userId: formInfo.userId,
            suspendedEmail: formInfo.suspendedEmail,
            isNewSubmission: true,
            isUpdatedSubmission: existingAppealNumberFound
          };

          setSubmissionInfo(newSubmissionInfo);
          setCurrentPage(AppealPage.Submission);
        }
      } catch (err: unknown) {
        // Re-throw 401 so the AFD CAPTCHA layer (captcha.submit) can intercept it
        // and trigger a new CAPTCHA challenge instead of showing a generic error.
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          throw err;
        }
        setHasSubmissionError(true);
      } finally {
        setIsRequestProcessing(false);
      }
    };

    if (isAfdWafEnabled) {
      captcha.submit(doSubmit);
    } else {
      await doSubmit();
    }
  }

  return (
    <div className="form-container">
      {isLoading &&
        <Spinner
          size="extra-large"
          label="Loading..."
          labelPosition="below" />
      }
      {hasInitialError &&
        <ErrorPage
          errorHeader={t('Common.we-apologize')}
          errorSubText={t('Common.problem-submitting-request')} />
      }
      {(!hasInitialError && !isLoading && initialAppealerInfo?.appealNumber) &&
        <AppealSubmission
          appealType={appealType}
          appealNumber={initialAppealerInfo.appealNumber}
          appealerName={initialAppealerInfo.appealerName}
          contactEmail={initialAppealerInfo.contactEmail}
          appealDate={initialAppealerInfo.appealDate}
          appealerJustification={initialAppealerInfo.appealerJustification}
          policyViolated={initialAppealerInfo.policyViolated}
          reportId={initialAppealerInfo.reportId}
          userId={""}  // Only needed for AccountAppeals (which we don't retrieve a 2nd time)
          suspendedEmail={""}
          isNewSubmission={false}
          isUpdatedSubmission={false} />
      }
      {(!hasInitialError && !isLoading && submissionInfo.appealNumber) &&
        <AppealSubmission
          appealType={appealType}
          appealNumber={submissionInfo.appealNumber}
          appealerName={submissionInfo.appealerName}
          contactEmail={submissionInfo.contactEmail}
          appealDate={submissionInfo.appealDate}
          appealerJustification={submissionInfo.appealerJustification}
          policyViolated={submissionInfo.policyViolated}
          reportId={submissionInfo.reportId}
          userId={submissionInfo.userId}
          suspendedEmail={submissionInfo.suspendedEmail}
          isNewSubmission={submissionInfo.isNewSubmission}
          isUpdatedSubmission={submissionInfo.isUpdatedSubmission} />
      }
      {(!hasInitialError && !isLoading && currentPage === AppealPage.Expiry) &&
        <AppealExpiry
          appealType={appealType}
          contactEmail={initialAppealerInfo.contactEmail}
          policyViolated={initialAppealerInfo.policyViolated}
          eligibilityPeriod={initialAppealerInfo.eligibilityPeriod} />
      }
      {(!hasInitialError && !isLoading && !initialAppealerInfo?.appealNumber && currentPage === AppealPage.Landing) &&
        <AppealLanding
          appealType={appealType}
          header={getAppealLandingHeader(appealType)}
          subHeader={getAppealLandingSubHeader(appealType)}
          subHeaderTwo={getAppealLandingSubHeaderTwo(appealType)} />
      }
      {(!hasInitialError && !isLoading && !initialAppealerInfo?.appealNumber && currentPage === AppealPage.Hip && shouldUseLegacyHip) &&
        <AppealHip
          header={t(getAppealLandingHeader(appealType))}
          hipSolved={hipSolved}
          handleCaptchaSubmit={handleCaptchaSubmit} />
      }
      {(!hasInitialError && !isLoading && !initialAppealerInfo?.appealNumber && currentPage === AppealPage.Form) &&
        <AppealForm
          appealType={appealType}
          formInfo={formInfo}
          nameChange={nameChange}
          emailChange={emailChange}
          justificationChange={justificationChange}
          userIdChange={userIdChange}
          suspendedEmailChange={suspendedEmailChange} />
      }
      {(!hasInitialError && !isLoading && !initialAppealerInfo?.appealNumber && currentPage === AppealPage.PreSubmission) &&
        <AppealPreSubmission
          appealType={appealType}
          formInfo={formInfo}
          nameChange={nameChange}
          emailChange={emailChange}
          justificationChange={justificationChange}
          productChange={productChange} />
      }
      {isRequestProcessing &&
        <Spinner
          size="extra-large"
          label="Loading..."
          labelPosition="below" />
      }
      {(hasHipError || hasSubmissionError) &&
        <ErrorBox errorMessage={t('Common.problem-submitting-request')} />
      }
      {currentPage === AppealPage.PreSubmission &&
        <div className="appeal-consent">
          <Checkbox
            className="appeal-consent-checkbox"
            id="appealCheckBox"
            data-testid="checkbox"
            checked={checkboxChecked}
            onChange={(ev, data) => checkboxChange(Boolean(data.checked))}
          />
          <Label className="appeal-consent-label" htmlFor="appealCheckBox">
            {t('Appeal.Common.checkbox-attest')}
          </Label>
        </div>
      }
      {(!hasInitialError && !isLoading && currentPage < AppealPage.Submission) &&
        <AppealButtonRow
          currentPage={currentPage}
          isRequestProcessing={isRequestProcessing}
          hipSolved={isAfdWafEnabled || hipSolved}
          formValid={formIsValid}
          preSubmissionValid={preSubmissionIsValid}
          handleContinue={handleContinue}
          handleBack={handleBack}
          handleSubmit={appealType === AppealType.Account ? handleAccountAppealSubmit : handleSubmit} />
      }
      {shouldUseAfdCaptcha &&
        <CaptchaPopup
          key={captcha.captchaKey}
          isVisible={captcha.isChallengeVisible}
          stubUrl={getStubUrl(env.REACT_APP_BACKEND_URL, appealType === AppealType.Account ? APPEALS_SOURCE.ACCOUNT : APPEALS_SOURCE.CONTENT)}
          captchaError={captcha.captchaError}
          onCancel={captcha.hideChallenge}
          onRetry={captcha.reRenderCaptcha}
        />
      }
    </div>
  )
}
